import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPublicRecipes,
  getRecipePoster,
  getUserRecipes,
  getUserSharedApps,
  createRecipe,
  deleteRecipe,
  duplicateRecipe,
  renameRecipe,
  changeOwner,
  getPublicTemplateRecipes,
} from '@/api/recipes';
import { deleteTool } from '@/api/toolView.api';
import { FOLDER_CONTENTS_QUERY_KEY } from '@/queries/useFolders';
import { toolViewQueryKey, useInvalidateToolByWorkflow } from '@/queries/useToolView';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_APP_VIEW_SEPARATION } from '@/consts/featureFlags';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { goToRecipe } from '@/utils/recipes';
import { RecipeType } from '@/enums/recipe-type.enum';
import type { DashboardRecipe } from '@/types/recipes';
import type { FolderScope } from '@/enums/folder-scope.enum';
import type { ListFolderResponseDto } from '@/types/folder.types';

const USER_RECIPES_QUERY_KEY = 'userRecipes';
const PUBLIC_RECIPES_QUERY_KEY = 'publicRecipes';
const PUBLIC_TEMPLATE_RECIPES_QUERY_KEY = 'publicTemplateRecipes';
const USER_SHARED_APPS_QUERY_KEY = 'userSharedApps';

export const useGetUserRecipes = () => {
  return useQuery({
    queryKey: [USER_RECIPES_QUERY_KEY],
    queryFn: getUserRecipes,
  });
};

export const useGetPublicRecipes = () => {
  return useQuery({
    queryKey: [PUBLIC_RECIPES_QUERY_KEY],
    queryFn: getPublicRecipes,
  });
};

/**
 * @param enabled - Pass the caller's feature-flag value so users off the flag don't pay for the fetch.
 */
export const useGetPublicTemplateRecipes = (enabled = true) => {
  return useQuery({
    queryKey: [PUBLIC_TEMPLATE_RECIPES_QUERY_KEY],
    queryFn: getPublicTemplateRecipes,
    enabled,
  });
};

export const useGetRecipePosters = (recipeIds: string[]) => {
  return useQuery({
    queryKey: ['recipePosters', recipeIds],
    queryFn: async () => {
      const entries = await Promise.all(recipeIds.map(async (id) => [id, await getRecipePoster(id)] as const));
      return new Map(entries);
    },
    enabled: recipeIds.length > 0,
  });
};

/**
 * Splits `/v1/recipes/shared` into the dashboard's shared sections.
 *
 * On the flag there is one section holding both kinds. A workflow and its published tool are separate
 * grants, so a workflow shared both ways arrives as one row per grant, each tagged with the grant it
 * stands for, and carrying a `rowId` because this is the one list where two rows share an `id`.
 * Splitting on `isApp` (merely "was published") instead once kept every shared published workflow out
 * of the section (WEA-6479). Off the flag the two sections keep their old contents.
 *
 * Either way a row carries only the pointer its kind stands for, because `useGoToWorkflow` opens a row
 * by what it holds. `??` on `sharedAsWorkflow` covers a server that predates the field.
 */
export const splitSharedRows = (
  workflows: DashboardRecipe[],
  { isToolViewEnabled, areTabsEnabled }: { isToolViewEnabled: boolean | undefined; areTabsEnabled: boolean },
): { sharedWithMe: DashboardRecipe[]; sharedApps: DashboardRecipe[] } => {
  const isUnifiedList = areTabsEnabled && isToolViewEnabled === true;
  const workflowShares = workflows
    .filter((workflow) => workflow.sharedAsWorkflow ?? workflow.isApp !== true)
    // Drop the tool cover too, so a workflow share keeps the canvas poster rather than the tool's.
    .map(({ toolId: _toolId, toolThumbnail: _toolThumbnail, ...workflow }) => workflow);

  // `isApp` is the legacy stand-in for a tool share and only says the workflow was published, so a
  // workflow share of a published workflow answers to it too. Harmless across two sections, but in
  // one list it would appear twice — so on the flag only a real `toolId` earns the tool kind.
  // A tool-only share has no canvas poster. The card reads `poster`, so the tool thumbnail becomes it.
  const toolShares = workflows
    .filter((workflow) => (isToolViewEnabled ? !!workflow.toolId : workflow.isApp))
    .map((workflow) => (workflow.toolThumbnail ? { ...workflow, poster: workflow.toolThumbnail } : workflow));

  if (!isUnifiedList) {
    return { sharedWithMe: workflowShares, sharedApps: toolShares };
  }

  return {
    sharedWithMe: [
      ...workflowShares.map((workflow) => ({
        ...workflow,
        // `useGoToWorkflow` reads this to keep the viewer on the canvas rather than the tool page.
        sharedAsWorkflow: true,
        workflowKind: 'workflow' as const,
        rowId: `workflow:${workflow.id}`,
      })),
      ...toolShares.map((workflow) => ({
        ...workflow,
        workflowKind: 'tool' as const,
        rowId: `tool:${workflow.id}`,
      })),
    ],
    // One section holds both kinds now, so nothing is left for a second one to show.
    sharedApps: [],
  };
};

export const useGetSharedWithMe = () => {
  const { value: isToolViewEnabled } = useFeatureFlag(FF_APP_VIEW_SEPARATION);
  const areTabsEnabled = useToolTabsEnabled();
  return useQuery({
    queryKey: [USER_SHARED_APPS_QUERY_KEY],
    queryFn: getUserSharedApps,
    select: useCallback(
      (workflows: DashboardRecipe[]) => splitSharedRows(workflows, { isToolViewEnabled, areTabsEnabled }),
      [isToolViewEnabled, areTabsEnabled],
    ),
  });
};

export function useCreateRecipe() {
  const qc = useQueryClient();

  return useMutation<
    DashboardRecipe,
    Error,
    { openInNewTab?: boolean; parentId?: string | null; scope?: FolderScope } | void
  >({
    mutationFn: (variables) => createRecipe(variables?.parentId, variables?.scope),

    onSuccess: (data, variables) => {
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        const prev = old ?? [];
        if (prev.some((i) => i.id === data.id)) return prev; // avoid dupes
        return [...prev, data];
      });
      goToRecipe(data.id, variables?.openInNewTab, true);
    },
  });
}

export function useDuplicateRecipe() {
  const qc = useQueryClient();

  return useMutation<DashboardRecipe, Error, string>({
    mutationFn: duplicateRecipe,
    onSuccess: (data) => {
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        const prev = old ?? [];
        if (prev.some((i) => i.id === data.id)) return prev; // avoid dupes
        return [...prev, data];
      });

      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}

export function useDeleteRecipe() {
  const qc = useQueryClient();
  const areTabsEnabled = useToolTabsEnabled();

  return useMutation<void, Error, { id: string; type: RecipeType }>({
    mutationFn: (variables) => deleteRecipe(variables.id, variables.type !== RecipeType.File),

    onSuccess: (_, variables) => {
      if (variables.type === RecipeType.File) {
        qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
          return old?.filter((recipe) => recipe.id !== variables.id) ?? [];
        });
      } else {
        // This entry holds the raw server list the sections derive from. On the flag it derives one
        // row per grant, so dropping the row outright would also take the Tool row of a both-ways
        // share — the dismiss hid the workflow grant alone. Off the flag, production's updater.
        qc.setQueryData<DashboardRecipe[]>([USER_SHARED_APPS_QUERY_KEY], (old) =>
          areTabsEnabled
            ? (old?.flatMap((recipe) => {
                if (recipe.id !== variables.id) return [recipe];
                return recipe.toolId ? [{ ...recipe, sharedAsWorkflow: false }] : [];
              }) ?? [])
            : (old?.filter((recipe) => recipe.id !== variables.id) ?? []),
        );
      }

      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}

/** Deletes a workflow's tool and keeps the workflow, which then lists as a plain workflow row. */
export function useDeleteTool() {
  const qc = useQueryClient();
  const invalidateToolByWorkflow = useInvalidateToolByWorkflow();

  return useMutation<void, Error, { toolId: string; workflowId: string }>({
    mutationFn: (variables) => deleteTool(variables.toolId),

    onSuccess: async (_, variables) => {
      const dropTool = (recipe: DashboardRecipe) =>
        recipe.id === variables.workflowId
          ? { ...recipe, toolId: undefined, toolThumbnail: undefined, toolName: undefined }
          : recipe;
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => old?.map(dropTool));
      // Edited in place rather than refetched: the listing reads a replica, which can still return the
      // deleted tool and put its row back. A fetch already in flight would do the same, so it goes first.
      await qc.cancelQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
      qc.setQueriesData<ListFolderResponseDto>({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] }, (old) =>
        old ? { ...old, recipes: old.recipes.map(dropTool) } : old,
      );
      void qc.invalidateQueries({ queryKey: toolViewQueryKey(variables.toolId) });
      void invalidateToolByWorkflow(variables.workflowId);
    },
  });
}

export function useRenameRecipe() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; newName: string }) => renameRecipe(variables.id, variables.newName),

    onMutate: (variables) => {
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        return old?.map((r) => (r.id === variables.id ? { ...r, name: variables.newName } : r)) ?? [];
      });

      qc.setQueriesData<ListFolderResponseDto>({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          recipes: old.recipes.map((r) => (r.id === variables.id ? { ...r, name: variables.newName } : r)),
        };
      });
    },

    onError: () => {
      void qc.invalidateQueries({ queryKey: [USER_RECIPES_QUERY_KEY] });
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}

export function useChangeRecipeOwner() {
  const qc = useQueryClient();

  return useMutation<void, Error, { id: string; newOwnerId: string; newOwnerName?: string }>({
    mutationFn: (variables) => changeOwner(variables.id, variables.newOwnerId),

    onSuccess: (_, variables) => {
      // Update the recipe in userRecipes with new owner
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        return (
          old?.map((r) =>
            r.id === variables.id ? { ...r, owner: variables.newOwnerId, ownerName: variables.newOwnerName } : r,
          ) ?? []
        );
      });

      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}
